import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import Decimal from "decimal.js";
import { api } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { formatMoney } from "../../lib/money";
import { todayIso } from "../leave/LeaveCalendar";
import type { QuotePaymentTerm, QuoteReceipt } from "../../api/types";
import { announceMoneyChanged } from "./MoneyStrip";
import { MoneyField } from "../../components/MoneyInput";

// 收款記在報價單的付款期別上(業主 2026-09-30,財務整理 B)。
//
// > 收款要怎麼記? —— 直接記在報價單的付款期別上
//
// 公司手開紙本發票,系統裡沒有發票,以前收款掛不上去。現在每一期「登錄收款」,
// 一期可以收好幾次;沒排付款條件的單也收得了錢(不指定期別)。
// **誰都能登錄**(業務最先收到匯款通知);刪:登錄的人、財務、老闆 —— 後端說了算(`can_delete`)。
//
// 只列**存過的**期別:還沒存檔的那一列沒有 id,收款掛不上去。

export interface ReceiptsProps {
  quoteId: number;
  /** 存過的付款期別(`quote.payment_terms`)。 */
  terms: QuotePaymentTerm[];
}

const NO_TERM = "";

export function Receipts({ quoteId, terms }: ReceiptsProps) {
  const toast = useToast();
  const [rows, setRows] = useState<QuoteReceipt[]>([]);
  const [open, setOpen] = useState(false);
  const [termId, setTermId] = useState(NO_TERM);
  const [paidOn, setPaidOn] = useState(todayIso());
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    api
      .get<QuoteReceipt[]>(`/quotes/${quoteId}/receipts`)
      .then((got) => setRows(Array.isArray(got) ? got : []))
      .catch(() => undefined);
  }, [quoteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 每一期收了多少。只是把這一張單的幾筆收款依期別分堆(整張單的已收 / 未收在後端算,見上方摘要)。
  const byTerm = useMemo(() => {
    const out = new Map<number | null, Decimal>();
    for (const r of rows) out.set(r.term_id, (out.get(r.term_id) ?? new Decimal(0)).plus(r.amount));
    return out;
  }, [rows]);

  const termName = (id: number | null) => terms.find((t) => t.id === id)?.name ?? "不指定期別";

  const start = (term?: QuotePaymentTerm) => {
    setTermId(term ? String(term.id) : NO_TERM);
    setPaidOn(todayIso());
    setAmount("");
    setNote("");
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/quotes/${quoteId}/receipts`, {
        paid_on: paidOn,
        amount: amount.replace(/,/g, "").trim(),
        term_id: termId === NO_TERM ? null : Number(termId),
        note: note.trim(),
      });
      toast.push("已登錄收款", "success");
      setOpen(false);
      refresh();
      announceMoneyChanged();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "登錄不了", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: QuoteReceipt) => {
    try {
      await api.del(`/receipts/${row.id}`);
      toast.push("已刪除(七天內可以到回收桶救回來)", "success");
      refresh();
      announceMoneyChanged();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "刪不掉", "error");
    }
  };

  return (
    <section id="quote-receipts" aria-label="收款" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-base font-semibold text-[var(--text)]">收款</h3>
        <Button size="sm" icon={<Plus aria-hidden="true" size={14} />} onClick={() => start()}>
          登錄收款
        </Button>
      </div>

      {terms.length > 0 ? (
        <div className="overflow-x-auto">
          <table aria-label="付款期別收款" className="w-full max-w-2xl border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-[var(--text-3)]">
                <th className="px-3 py-2 font-medium">期別</th>
                <th className="px-3 py-2 text-right font-medium">應收</th>
                <th className="px-3 py-2 font-medium">到期日</th>
                <th className="px-3 py-2 text-right font-medium">已收</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {terms.map((t) => (
                <tr key={t.id} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2">{t.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(t.resolved_amount)}</td>
                  <td className="px-3 py-2 tabular-nums">{t.due_date ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney((byTerm.get(t.id) ?? new Decimal(0)).toFixed(2))}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" aria-label={`登錄收款:${t.name}`} onClick={() => start(t)}>
                      登錄收款
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="m-0 text-sm text-[var(--text-3)]">這張單沒有排付款條件,收款直接登錄在整張單上。</p>
      )}

      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table aria-label="收款紀錄" className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-[var(--text-3)]">
                <th className="px-3 py-2 font-medium">收款日</th>
                <th className="px-3 py-2 font-medium">期別</th>
                <th className="px-3 py-2 text-right font-medium">金額</th>
                <th className="px-3 py-2 font-medium">備註</th>
                <th className="px-3 py-2 font-medium">登錄者</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2 tabular-nums">{r.paid_on}</td>
                  <td className="px-3 py-2">{termName(r.term_id)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(r.amount)}</td>
                  <td className="px-3 py-2">{r.note}</td>
                  <td className="px-3 py-2">{r.recorder_name}</td>
                  <td className="px-3 py-2 text-right">
                    {r.can_delete ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="刪除這筆收款"
                        icon={<Trash2 aria-hidden="true" size={14} />}
                        onClick={() => {
                          void remove(r);
                        }}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="登錄收款"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button
              variant="primary"
              loading={saving}
              disabled={!amount.trim() || !paidOn}
              onClick={() => {
                void submit();
              }}
            >
              登錄
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Select
            label="期別"
            value={termId}
            onChange={(e) => setTermId(e.target.value)}
            options={[
              { value: NO_TERM, label: "不指定期別" },
              ...terms.map((t) => ({ value: String(t.id), label: `${t.name}(${formatMoney(t.resolved_amount)})` })),
            ]}
          />
          <Input label="收款日" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          <MoneyField label="金額" value={amount} onValueChange={setAmount} />
          <Input label="備註" placeholder="例:匯款、支票" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Dialog>
    </section>
  );
}
